import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Trash2, Wrench } from "lucide-react";
import {
  formatDate,
  ORDER_STATUS_LABELS,
  PROPERTY_SYSTEM_TYPE_LABELS,
} from "@mavatech/shared";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  ConfirmDialog,
  Input,
  Select,
  SortTh,
  useSort,
  LoadError,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { OrderDialog } from "./OrderDialog";

const STATUS_TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  quoted: "neutral",
  ordered: "accent",
  done: "success",
  cancelled: "danger",
};

const STATUS_OPTIONS = [
  { value: "", label: "Alle Status" },
  ...Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

const DOCS_OPTIONS = [
  { value: "", label: "Alle" },
  { value: "quote", label: "Mit Angebot" },
  { value: "no_quote", label: "Ohne Angebot" },
  { value: "invoice", label: "Mit Rechnung" },
  { value: "no_invoice", label: "Ohne Rechnung" },
];

export function Orders() {
  const utils = trpc.useUtils();
  const addToast = useAppStore((s) => s.addToast);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [assigneeUserId, setAssigneeUserId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [systemType, setSystemType] = useState("");
  const [docs, setDocs] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [dialog, setDialog] = useState<{
    open: boolean;
    orderId?: string | null;
  }>({ open: false });
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  // Der geöffnete Auftrag steht in der URL (?open=<id>): so führt "Zurück" aus einem Dokument, das
  // man über das Auftragsjournal geöffnet hat, wieder in denselben Auftrag. Gleichzeitig ist das der
  // Deep-Link der globalen Schnellsuche (Cmd/Strg+K). replace statt push, damit Öffnen/Schließen
  // keine zusätzlichen Verlaufseinträge erzeugt.
  const openId = params.get("open");
  const openOrder = (id: string) => setParams({ open: id }, { replace: true });
  const dialogState = openId ? { open: true, orderId: openId } : dialog;
  const closeDialog = () => {
    setDialog({ open: false });
    if (openId) setParams({}, { replace: true });
  };

  const list = trpc.order.list.useQuery({});
  const members = trpc.auth.members.useQuery();
  const remove = trpc.order.remove.useMutation({
    onSuccess: () => {
      utils.order.list.invalidate();
      addToast({ type: "success", title: "Auftrag gelöscht" });
    },
  });

  const properties = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    for (const o of list.data ?? []) {
      if (o.property)
        map.set(o.property.id, { id: o.property.id, label: o.property.label });
    }
    return [...map.values()];
  }, [list.data]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (list.data ?? []).filter((o) => {
      if (s) {
        const hay = [
          o.number,
          o.title,
          o.customer.name,
          o.property?.addressStreet,
          o.property?.addressCity,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (status && o.status !== status) return false;
      if (assigneeUserId && o.assignee?.id !== assigneeUserId) return false;
      if (propertyId && o.property?.id !== propertyId) return false;
      if (systemType && o.property?.systemType !== systemType) return false;
      if (docs === "quote" && !o.quote) return false;
      if (docs === "no_quote" && o.quote) return false;
      if (docs === "invoice" && !o.invoices.length) return false;
      if (docs === "no_invoice" && o.invoices.length) return false;
      if (
        from &&
        (!o.scheduledDate || new Date(o.scheduledDate) < new Date(from))
      )
        return false;
      if (
        to &&
        (!o.scheduledDate ||
          new Date(o.scheduledDate) > new Date(`${to}T23:59:59`))
      )
        return false;
      return true;
    });
  }, [
    list.data,
    search,
    status,
    assigneeUserId,
    propertyId,
    systemType,
    docs,
    from,
    to,
  ]);

  const { sorted, sort, toggle } = useSort(filtered, {
    number: (o) => o.number,
    title: (o) => o.title,
    customer: (o) => o.customer.name,
    property: (o) => o.property?.label,
    docs: (o) => o.invoices.length + (o.quote ? 1 : 0),
    date: (o) => o.scheduledDate,
    assignee: (o) => o.assignee?.name ?? o.assignee?.email,
    status: (o) => o.status,
  });

  const hasFilters =
    search ||
    status ||
    assigneeUserId ||
    propertyId ||
    systemType ||
    docs ||
    from ||
    to;

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Aufträge"
        actions={
          <Button variant="primary" onClick={() => setDialog({ open: true })}>
            <Plus className="w-4 h-4" /> Neuer Auftrag
          </Button>
        }
      />
      <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Betreff, Nummer, Kunde, Adresse …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          fullWidth={false}
          className="w-40"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={STATUS_OPTIONS}
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={assigneeUserId}
          onChange={(e) => setAssigneeUserId(e.target.value)}
          options={[
            { value: "", label: "Alle Techniker" },
            ...(members.data ?? []).map((m) => ({
              value: m.userId,
              label: m.name,
            })),
          ]}
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          options={[
            { value: "", label: "Alle Objekte" },
            ...properties.map((p) => ({ value: p.id, label: p.label })),
          ]}
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={systemType}
          onChange={(e) => setSystemType(e.target.value)}
          options={[
            { value: "", label: "Alle Anlagentypen" },
            ...Object.entries(PROPERTY_SYSTEM_TYPE_LABELS).map(
              ([value, label]) => ({ value, label }),
            ),
          ]}
        />
        <Select
          fullWidth={false}
          className="w-40"
          value={docs}
          onChange={(e) => setDocs(e.target.value)}
          options={DOCS_OPTIONS}
        />
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          title="Termin von"
        />
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          title="Termin bis"
        />
        {hasFilters && (
          <button
            className="text-xs text-text-tertiary hover:text-text-primary"
            onClick={() => {
              setSearch("");
              setStatus("");
              setAssigneeUserId("");
              setPropertyId("");
              setSystemType("");
              setDocs("");
              setFrom("");
              setTo("");
            }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !filtered.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            {hasFilters ? "Keine Treffer." : "Noch keine Aufträge erfasst."}
          </div>
        ) : (
          <>
            {/* Mobil: Karten statt Tabelle (AUDIT-2026-10-01-UX.md, Punkt 3.3). */}
            <div className="md:hidden divide-y divide-border-subtle">
              {sorted.map((o) => (
                <button
                  key={o.id}
                  onClick={() => openOrder(o.id)}
                  className="w-full text-left px-4 py-3 flex flex-col gap-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-text-tertiary">
                      {o.number ?? "—"}
                    </span>
                    <Badge tone={STATUS_TONE[o.status] ?? "neutral"}>
                      {ORDER_STATUS_LABELS[
                        o.status as keyof typeof ORDER_STATUS_LABELS
                      ] ?? o.status}
                    </Badge>
                  </div>
                  <div className="font-medium text-text-primary flex items-center gap-1.5">
                    <Wrench className="w-3.5 h-3.5 text-text-tertiary shrink-0" />{" "}
                    {o.title}
                  </div>
                  <div className="text-text-tertiary text-xs">
                    {o.customer.name}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-1 text-xs text-text-tertiary">
                    <span>
                      {o.scheduledDate
                        ? formatDate(o.scheduledDate as unknown as string)
                        : "kein Termin"}
                    </span>
                    <span>{o.assignee?.name ?? o.assignee?.email ?? "—"}</span>
                  </div>
                </button>
              ))}
            </div>
            <table className="data-table hidden md:table">
              <thead>
                <tr>
                  <SortTh
                    label="Nummer"
                    sortKey="number"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Betreff"
                    sortKey="title"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Kunde"
                    sortKey="customer"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Objekt"
                    sortKey="property"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Dokumente"
                    sortKey="docs"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Termin"
                    sortKey="date"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Techniker"
                    sortKey="assignee"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Status"
                    sortKey="status"
                    sort={sort}
                    onSort={toggle}
                  />
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((o) => (
                  <tr
                    key={o.id}
                    className="cursor-pointer"
                    onClick={() => openOrder(o.id)}
                  >
                    <td className="font-mono text-text-secondary">
                      {o.number ?? "—"}
                    </td>
                    <td className="font-medium text-text-primary flex items-center gap-1.5">
                      <Wrench className="w-3.5 h-3.5 text-text-tertiary" />{" "}
                      {o.title}
                    </td>
                    <td className="text-text-secondary">{o.customer.name}</td>
                    <td className="text-text-tertiary text-xs">
                      {o.property?.label ?? "—"}
                    </td>
                    <td className="text-2xs">
                      <span className="flex items-center gap-1">
                        {o.quote && (
                          <Badge tone="neutral">
                            {o.quote.number ?? "Angebot"}
                          </Badge>
                        )}
                        {o.invoices.length > 0 && (
                          <Badge tone="accent">
                            {o.invoices.length}{" "}
                            {o.invoices.length === 1
                              ? "Rechnung"
                              : "Rechnungen"}
                          </Badge>
                        )}
                        {!o.quote && !o.invoices.length && (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </span>
                    </td>
                    <td className="text-text-secondary">
                      {o.scheduledDate
                        ? formatDate(o.scheduledDate as unknown as string)
                        : "—"}
                    </td>
                    <td className="text-text-secondary">
                      {o.assignee?.name ?? o.assignee?.email ?? "—"}
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[o.status] ?? "neutral"}>
                        {ORDER_STATUS_LABELS[
                          o.status as keyof typeof ORDER_STATUS_LABELS
                        ] ?? o.status}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <button
                        className="text-text-tertiary hover:text-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmRemove(o.id);
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <OrderDialog state={dialogState} onClose={closeDialog} />
      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(v) => !v && setConfirmRemove(null)}
        title="Auftrag löschen?"
        message="Der Auftrag wird ausgeblendet (Soft-Delete)."
        confirmLabel="Löschen"
        danger
        onConfirm={() => {
          if (confirmRemove) remove.mutate({ id: confirmRemove });
          setConfirmRemove(null);
        }}
      />
    </div>
  );
}
